import { ReactElement } from 'react'
import { useLocation } from 'react-router-dom'
import getAccountCardDetails from 'helpers/getAccountCardDetails'
import PaymentButton from 'pages/AccountsDashboard/Card/actions/PaymentButton'
import shouldShowAccountServiceButton from 'pages/AccountServices/shouldShowAccountServiceButton'
import useWindowSize from 'syf-component-library/ui/customHooks/useWindowWidth'
import { Stack } from 'syf-component-library/ui/spacing'
import { Account } from '@syf-api-library/credit-accounts'
import { AccountSummaryGetResponse } from '@syf-api-library/account-summary'

import { useAccountFeatures } from 'const/syfApiQueries'
import isFeatureFlagEnabled from 'helpers/isFeatureFlagEnabled'
import isLoanAccount from 'helpers/isLoanAccount'
import { SAMS } from 'const/account/clients'
import PaymentFieldsDetail from './PaymentFieldsDetail'
import MobileMenuNavigation from './MobileMenuNavigation'
import { ButtonContainer, PaymentWrapper } from './subcomponents'

interface PaymentDetailHeaderProps {
  accountSummary: AccountSummaryGetResponse
  account: Account
}

const paymentButtonDataType = {
  'account-services': 'account services',
  activity: 'activity'
}

const PaymentDetailHeader = ({
  accountSummary,
  account
}: PaymentDetailHeaderProps): ReactElement => {
  const windowWidth = useWindowSize()
  const location = useLocation()

  const cardDetails = accountSummary
    ? getAccountCardDetails(accountSummary)
    : []

  const { data: accountFeatures } = useAccountFeatures(
    account?.info?.['cipher.accountId']
  )

  const showAccountServiceMenu =
    accountFeatures?.accountServicingEnabled &&
    shouldShowAccountServiceButton(accountSummary)

  const { accessToRewardsMfe, accessToRewards } =
    accountFeatures?.rewardDetails || {}
  const isSamsClient = accountSummary?.product?.clientId === SAMS

  /*
   * Sam's Club: show Rewards only when isSamsRewardsMfeEnabled flag is ON.
   * Non-Sam's Club: rely on existing conditions.
   */
  const isRewardsButtonEnabled =
    accessToRewardsMfe &&
    accessToRewards &&
    (!isSamsClient || isFeatureFlagEnabled('isSamsRewardsMfeEnabled'))

  const isPromotionsButtonEnabled = accountFeatures?.accessToPromotions

  const isAccountTypeLoan = isLoanAccount(accountSummary?.product?.type)
  const isStatementsButtonEnabled =
    accountFeatures?.accessToStatements && !isAccountTypeLoan

  return (
    <PaymentWrapper data-cy="account-header-payment-details">
      <PaymentFieldsDetail detail={cardDetails[0]} />
      <PaymentFieldsDetail detail={cardDetails[1]} />
      <PaymentFieldsDetail detail={cardDetails[2]} />
      <PaymentFieldsDetail detail={cardDetails[3]} />
      <Stack hideOnBreakpointUp="small" />
      {accountSummary && account && (
        <ButtonContainer>
          <PaymentButton
            className="make-payment-btn"
            account={account}
            accountSummary={accountSummary}
            dataType={paymentButtonDataType[location.pathname.replace('/', '')]}
          />
        </ButtonContainer>
      )}
      {windowWidth.isLessThanMobile && (
        <MobileMenuNavigation
          isStatementsButtonEnabled={isStatementsButtonEnabled}
          isPromotionsButtonEnabled={isPromotionsButtonEnabled}
          showAccountServiceMenu={showAccountServiceMenu}
          isRewardsButtonEnabled={isRewardsButtonEnabled}
        />
      )}
    </PaymentWrapper>
  )
}

export default PaymentDetailHeader
